import { useId } from "react";

import { SwitchVariant } from "~/services";
import { icons } from "~/ui";
import { tw } from "~/utils";

export interface SubmissionSwitchOption {
  id: string;
  label: string;
  secondaryLabel: string;
}

interface SubmissionCardSwitchProps {
  options: SubmissionSwitchOption[];
  onChange: (selectedOption: string | null) => void;
  label?: string;
  selectedOption: string;
  error?: string;
  switchVariant?: SwitchVariant;
}

export const SubmissionCardSwitch = ({
  options,
  onChange,
  label,
  selectedOption,
  error,
  switchVariant = "primary",
}: SubmissionCardSwitchProps) => {
  const getOptionLabel = (option: SubmissionSwitchOption) =>
    switchVariant === "secondary" ? option.secondaryLabel : option.label;

  const errorId = `${useId()}-error`;

  return (
    <div className="space-y-2">
      {label && (
        <p className="text-indigo-charcoal font-normal leading-5">{label}</p>
      )}
      <fieldset aria-label="Server size">
        <div className="flex h-10 w-56 gap-2">
          {options.map((option) => {
            const isChecked = selectedOption === option.id;
            const optionLabel = getOptionLabel(option);
            return (
              <label
                key={option.id}
                className={tw(
                  "border-blueGrey group relative flex h-10 w-28 cursor-pointer items-center justify-center rounded-lg border bg-white p-3 text-gray-700 transition-all duration-200 hover:border-violet-300 hover:bg-neutral-100/50",
                  isChecked && "border-violet bg-violet/10 text-white",
                  error &&
                    "border-red-500 focus:border-red-500 focus:ring-red-500",
                )}
              >
                <input
                  type="radio"
                  value={option.id}
                  checked={isChecked}
                  onChange={(e) => onChange(e.target.value)}
                  aria-label={optionLabel}
                  aria-invalid={Boolean(error)}
                  aria-describedby={error ? errorId : undefined}
                  className="absolute inset-0 cursor-pointer appearance-none focus:outline-none"
                />
                <span
                  aria-hidden="true"
                  className={tw(
                    "text-midnight-indigo text-sm font-normal",
                    isChecked && "text-violet",
                  )}
                >
                  {optionLabel}
                </span>
              </label>
            );
          })}
        </div>
      </fieldset>
      {switchVariant === "secondary" && (
        <p className="text-xs font-normal text-neutral-900">
          Unsure? Select "yes" to share up-to-date info.
        </p>
      )}
      {error && (
        <p
          id={errorId}
          className="flex items-center gap-1 text-sm text-red-600"
        >
          <icons.outline.ExclamationCircleIcon className="size-4 min-w-4" />
          {error}
        </p>
      )}
    </div>
  );
};
